import { GridCellParams } from '@mui/x-data-grid-pro';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { GridCustomColDef } from '../../types';
import {
  advancedOperators,
  advancedDatePickerOperator,
  advancedEnumOperators
} from '../../Compounds/UpstackDataGrid/helpers';
import { getFormattedDate, getDate } from '../../../utils/helpers';
import { TicketFollowUpResult, TicketFollowUpEscalation } from '../../../store/TFU';
import { TicketStatus, TicketInquiryType, TicketResolutionType } from '../../../store/Ticket';

export const DefaultTFUGridColumnDefs = (): GridCustomColDef[] => [
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'ticketNumber',
    headerName: 'Ticket',
    renderCell: (params: GridCellParams) => (
      <Link
        className="text-indigo"
        title="Fullscreen"
        to={`/ticket/${params.row.ticketId}`}>
        <div className="flex items-center text-small">{params.value as string}</div>
      </Link>
    ),
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    pinned: 'left',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'dueDate',
    headerName: 'Due',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'startedDate',
    headerName: 'Started',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: Object.values(TicketFollowUpEscalation || {}),
    field: 'escalation',
    headerName: 'Escalation',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedEnumOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: Object.values(TicketFollowUpResult || {}),
    field: 'result',
    headerName: 'Result',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedEnumOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'note',
    headerName: 'Note',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'tsdName',
    headerName: 'TSD',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'supplierName',
    headerName: 'Supplier',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: Object.values(TicketStatus || {}),
    field: 'status',
    headerName: 'Status',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedEnumOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: Object.values(TicketInquiryType || {}),
    field: 'inquiryType',
    headerName: 'Inquiry Type',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedEnumOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'owner.email',
    headerName: 'Owner',
    sortable: false,
    filterable: true,
    width: 170,
    renderCell: (params: GridCellParams) => <span title={params.row.owner?.email || ''}>{params.value as string}</span>,
    valueGetter: (_value, row) => `${row?.owner?.firstName || ''} ${row?.owner?.lastName || ''}`,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'assignedTo.email',
    headerName: 'Ticket Assigned To',
    sortable: false,
    filterable: true,
    width: 170,
    renderCell: (params: GridCellParams) => (
      <span title={params.row.assignedTo?.email || ''}>{params.value as string}</span>
    ),
    valueGetter: (_value, row) => `${row?.assignedTo?.firstName || ''} ${row?.assignedTo?.lastName || ''}`,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'sourceTrackingNumber',
    headerName: 'Source Tracking Number',
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'description',
    headerName: 'Description',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'receivedDate',
    headerName: 'Received Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'submittedToSourceDate',
    headerName: 'Submitted to Source Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'expectedResolutionDate',
    headerName: 'Expected Resolution Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'actualResolutionDate',
    headerName: 'Actual Resolution Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: Object.values(TicketResolutionType || {}),
    field: 'resolutionType',
    headerName: 'Resolution Type',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedEnumOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'customerList',
    headerName: 'Customer List',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      if (!value) return '';
      return (value as Array<{ id: string; name: string; sf_id: string }>).map((v) => v.name).join(', ');
    },
    width: 170,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'apList',
    headerName: 'AP List',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      if (!value) return '';
      return (value as Array<{ id: string; name: string; sf_id: string }>).map((v) => v.name).join(', ');
    },
    width: 170,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'ticketCreatedDate',
    headerName: 'Created Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'latestComment',
    headerName: 'Latest Comment',
    renderCell: (params: GridCellParams) => (
      <div className="text-black ql-editor !p-0 -mt-[5px] !h-[initial] max-h-full inline-block align-middle !leading-[1.4]">
        {parse(DOMPurify.sanitize((params.value as string) || ''))}
      </div>
    ),
    sortable: true,
    filterable: true,
    width: 300,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'latestCommentBy',
    headerName: 'Latest Comment By',
    sortable: true,
    filterable: true,
    width: 170,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'latestCommentAt',
    headerName: 'Latest Comment At',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      return getFormattedDate(value, 'lll');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    width: 200,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedDatePickerOperator()
  }
];
